import { useCallback, useEffect, useRef, useState } from 'react'

export const TIP_KEYS = ['set_going'] as const
export type TipKey = (typeof TIP_KEYS)[number]

// One-shots scoped to an entity (an event, say) rather than the device: the
// storage key carries the id, so each instance is its own tip.
export const KEYED_TIP_KEYS = ['festival_totem'] as const
export type KeyedTipKey = (typeof KEYED_TIP_KEYS)[number]

// Rationed tips: at most once per session and a few times per device before
// they stay quiet for good — a coachmark claimed when its moment arrives
// (`claimRationedTip`), which doing the thing it teaches retires.
export const PEEK_KEYS = ['set_trim_conflict', 'schedule_filter'] as const
export type PeekKey = (typeof PEEK_KEYS)[number]

// FAB label peeks: a brief passive reveal on mount (`usePeekTip`), once per
// session with no device cap — a few showings proved too few to teach the icons.
export const LABEL_PEEK_KEYS = ['schedule_fab_labels', 'home_crew_fab_label'] as const
export type LabelPeekKey = (typeof LABEL_PEEK_KEYS)[number]
const PEEK_MAX_TOTAL = 3

const STORAGE_PREFIX = 'beacon_tip_'
const RESET_EVENT = 'beacon_tips_reset'

function storageKey(key: TipKey) {
  return STORAGE_PREFIX + key
}

function readShown(key: TipKey): boolean {
  try {
    return localStorage.getItem(storageKey(key)) === '1'
  } catch {
    return false
  }
}

function writeShown(key: TipKey) {
  try {
    localStorage.setItem(storageKey(key), '1')
  } catch {
    // Storage unavailable (private mode quota, disabled) — tip will reappear
    // next mount, which is acceptable for a passive coachmark.
  }
}

function keyedStorageKey(base: KeyedTipKey, id: string) {
  return `${STORAGE_PREFIX}${base}_${id}`
}

export function readKeyedTip(base: KeyedTipKey, id: string): boolean {
  try {
    return localStorage.getItem(keyedStorageKey(base, id)) === '1'
  } catch {
    return false
  }
}

export function writeKeyedTip(base: KeyedTipKey, id: string) {
  try {
    localStorage.setItem(keyedStorageKey(base, id), '1')
  } catch {
    // Same trade-off as writeShown: the tip reappears next mount.
  }
}

function keyedTipKeys(): string[] {
  const prefixes = KEYED_TIP_KEYS.map(base => `${STORAGE_PREFIX}${base}_`)
  const keys: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && prefixes.some(p => key.startsWith(p))) keys.push(key)
  }
  return keys
}

function peekCountKey(key: PeekKey) {
  return `${STORAGE_PREFIX}${key}_count`
}

function peekSessionKey(key: PeekKey | LabelPeekKey) {
  return `${STORAGE_PREFIX}${key}_session`
}

function canPeek(key: PeekKey): boolean {
  try {
    if (sessionStorage.getItem(peekSessionKey(key)) === '1') return false
    return Number(localStorage.getItem(peekCountKey(key)) ?? 0) < PEEK_MAX_TOTAL
  } catch {
    return false
  }
}

function consumePeek(key: PeekKey) {
  try {
    sessionStorage.setItem(peekSessionKey(key), '1')
    const count = Number(localStorage.getItem(peekCountKey(key)) ?? 0)
    localStorage.setItem(peekCountKey(key), String(count + 1))
  } catch {
    // Storage unavailable — the peek may repeat, which is harmless for a
    // passive reveal.
  }
}

function claimLabelPeek(key: LabelPeekKey): boolean {
  try {
    if (sessionStorage.getItem(peekSessionKey(key)) === '1') return false
    sessionStorage.setItem(peekSessionKey(key), '1')
  } catch {
    // Storage unavailable — the peek may repeat, which is harmless.
  }
  return true
}

/**
 * Spends one showing of a rationed tip if any is left. Reads storage at call
 * time rather than from state, so a retire from anywhere since mount is honored.
 */
export function claimRationedTip(key: PeekKey): boolean {
  if (!canPeek(key)) return false
  consumePeek(key)
  return true
}

/** The user found the feature on their own — never show its tip again. */
export function retireRationedTip(key: PeekKey) {
  try {
    localStorage.setItem(peekCountKey(key), String(PEEK_MAX_TOTAL))
  } catch {
    // Storage unavailable — the tip may show again, which is harmless.
  }
}

export function resetAllTips() {
  try {
    for (const k of TIP_KEYS) localStorage.removeItem(storageKey(k))
    for (const k of keyedTipKeys()) localStorage.removeItem(k)
    for (const k of PEEK_KEYS) {
      localStorage.removeItem(peekCountKey(k))
      sessionStorage.removeItem(peekSessionKey(k))
    }
    for (const k of LABEL_PEEK_KEYS) sessionStorage.removeItem(peekSessionKey(k))
    localStorage.removeItem('beacon:app_upsell')
  } catch {
    // ignore
  }
  window.dispatchEvent(new Event(RESET_EVENT))
}

export function useOneShotTip(key: TipKey) {
  const [shown, setShown] = useState(() => readShown(key))

  useEffect(() => {
    function onReset() {
      setShown(readShown(key))
    }
    window.addEventListener(RESET_EVENT, onReset)
    return () => window.removeEventListener(RESET_EVENT, onReset)
  }, [key])

  const markShown = useCallback(() => {
    writeShown(key)
    setShown(true)
  }, [key])

  return { shown, markShown }
}

// `peeking` is true for `hold` ms, starting `delay` ms after mount, the first
// time `key` mounts this session — spent on mount, not on completion, so a
// user who navigates away mid-peek isn't shown it again this session. `endPeek`
// cuts it short (the user found the control on their own).
export function usePeekTip(key: LabelPeekKey, { delay = 500, hold = 3000 } = {}) {
  const [peeking, setPeeking] = useState(false)
  // Survives StrictMode's double effect run, which would otherwise spend the
  // session ration on the first pass and skip the peek on the second.
  const consumed = useRef(false)
  const ended = useRef(false)

  useEffect(() => {
    if (!consumed.current) {
      if (!claimLabelPeek(key)) return
      consumed.current = true
    }
    const start = setTimeout(() => {
      if (!ended.current) setPeeking(true)
    }, delay)
    const stop = setTimeout(() => setPeeking(false), delay + hold)
    return () => {
      clearTimeout(start)
      clearTimeout(stop)
    }
  }, [key, delay, hold])

  const endPeek = useCallback(() => {
    ended.current = true
    setPeeking(false)
  }, [])
  return { peeking, endPeek }
}
